import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { formatInTimeZone } from 'date-fns-tz';
import { ReportControllerInsightPartnerService } from '@generated-apis/api/reportController-insightpartner.service';
import { ReportFileNameDetails } from './report-download.models';

@Injectable({
  providedIn: 'root',
})
export class ReportDownloadService {
  private readonly reportService = inject(ReportControllerInsightPartnerService);

  downloadReport(downloadPath: string): Observable<Blob> {
    const fileUuid = this.extractFileUuid(downloadPath);

    return this.reportService
      .downloadReportFile(fileUuid)
      .pipe(map((response) => new Blob([response], { type: 'application/pdf' })));
  }

  saveBlob(blob: Blob, fileName: string): void {
    const anchor = document.createElement('a');
    const objectUrl = URL.createObjectURL(blob);
    anchor.href = objectUrl;
    anchor.download = fileName;
    anchor.click();
    URL.revokeObjectURL(objectUrl);
  }

  resolveFileName(details: ReportFileNameDetails, downloadPath: string): string {
    const { serialNumber, geaPUID, startDate, stopDate, timezone } = details;

    if (geaPUID && startDate && stopDate && timezone) {
      return this.buildPdfName(
        serialNumber ?? '',
        geaPUID,
        formatInTimeZone(startDate, timezone, 'yyyy-MM-dd'),
        formatInTimeZone(stopDate, timezone, 'yyyy-MM-dd')
      );
    }

    return this.extractFileUuid(downloadPath);
  }

  buildPdfName(serialNumber: string, geaPUID: string, startDate: string, endDate: string): string {
    const serialNumberFormatted = serialNumber.length ? `${serialNumber}__` : '';

    return `${serialNumberFormatted}${geaPUID}__${startDate}__${endDate}__Report.pdf`;
  }

  extractFileUuid(downloadPath: string): string {
    return decodeURIComponent(downloadPath.split('/').pop() ?? downloadPath);
  }
}
